import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { ref, get } from 'firebase/database';

type BotType = 'whatsapp' | 'website' | 'voice' | 'facebook';

interface Template {
  id: string;
  name: string;
  description: string;
  botType: BotType;
  industry: string;
  icon: string;
  triggers: any[];
  greeting?: string;
  offlineMessage?: string;
  language?: string;
  isPublic: boolean;
  createdBy?: string;
}

interface TemplateSelectorProps {
  botType: BotType;
  onApply: (template: Template) => void;
  /** If true, the selector is shown as an inline panel instead of a modal */
  inline?: boolean;
}

const S = {
  input: { width: '100%', padding: '8px 12px', background: '#0f1117', border: '1px solid #1e2330', borderRadius: '8px', color: '#e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const },
};

const TYPE_COLORS: Record<BotType, string> = {
  whatsapp: '#10b981',
  website: '#3b82f6',
  voice: '#8b5cf6',
  facebook: '#1877f2',
};

export default function TemplateSelector({ botType, onApply, inline = false }: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Template | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [botType]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const snap = await get(ref(db, 'templates'));
      const list: Template[] = [];
      if (snap.exists()) {
        snap.forEach(c => {
          const t = { id: c.key!, ...c.val() } as Template;
          const isOwn = t.createdBy === auth.currentUser?.uid;
          if (t.botType === botType && (t.isPublic !== false || isOwn)) {
            list.push(t);
          }
        });
      }
      setTemplates(list);
    } catch (e) {
      console.error('Error loading templates:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = templates.filter(t =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.industry.toLowerCase().includes(search.toLowerCase()) ||
    t.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (tmpl: Template) => {
    setSelected(tmpl);
    setConfirming(true);
  };

  const handleConfirm = () => {
    if (!selected) return;
    onApply(selected);
    setConfirming(false);
    setSelected(null);
    setIsOpen(false);
  };

  const color = TYPE_COLORS[botType];

  // ── Confirm dialog ──
  if (confirming && selected) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
        <div style={{ background: '#15181f', borderRadius: '16px', border: '1px solid #1e2330', padding: '28px', maxWidth: '420px', width: '90%' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>{selected.icon}</div>
            <div style={{ color: '#e2e8f0', fontSize: '17px', fontWeight: '700', marginBottom: '6px' }}>
              Load "{selected.name}"?
            </div>
            <div style={{ color: '#475569', fontSize: '13px', lineHeight: '1.6' }}>
              This will replace your current flow with the <strong style={{ color: '#94a3b8' }}>{selected.name}</strong> template ({selected.triggers?.length || 0} triggers).
              <br />
              <span style={{ color: '#ef4444' }}>Any unsaved changes will be lost.</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button
              onClick={() => { setConfirming(false); setSelected(null); }}
              style={{ padding: '10px', background: '#1e2330', color: '#94a3b8', border: '1px solid #1e2330', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              style={{ padding: '10px', background: color, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}
            >
              Yes, load template
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Trigger button (inline mode) ──
  if (!isOpen && !inline) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '7px 14px', background: color + '15', color,
          border: `1px solid ${color}40`, borderRadius: '8px',
          cursor: 'pointer', fontSize: '12px', fontWeight: '600',
        }}
      >
        📋 Load template
      </button>
    );
  }

  // ── Full panel ──
  const panel = (
    <div style={{
      background: '#15181f', borderRadius: '16px', border: '1px solid #1e2330',
      display: 'flex', flexDirection: 'column',
      ...(inline ? { overflow: 'hidden' } : {
        position: 'fixed',
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        width: '90vw', maxWidth: '640px',
        height: '80vh', maxHeight: '720px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
      }),
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e2330', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ color: '#e2e8f0', fontSize: '15px', fontWeight: '700' }}>
            📋 Choose a template
          </div>
          <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>
            {templates.length} {botType} templates available
          </div>
        </div>
        {!inline && (
          <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e2330' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or industry..."
          style={S.input}
          autoFocus
        />
      </div>

      {/* List */}
      <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#475569', fontSize: '13px' }}>
            Loading templates...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
            <div style={{ color: '#475569', fontSize: '13px' }}>
              {templates.length === 0
                ? `No ${botType} templates found. Ask your admin to add some in Template Manager.`
                : 'No templates match your search.'}
            </div>
          </div>
        )}

        {!loading && filtered.map(tmpl => (
          <div
            key={tmpl.id}
            onClick={() => handleSelect(tmpl)}
            style={{
              display: 'flex', gap: '14px', alignItems: 'flex-start',
              padding: '14px', borderRadius: '11px', cursor: 'pointer',
              border: '1px solid #1e2330', marginBottom: '8px',
              background: '#12111A',
              transition: 'all 0.1s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = color + '60')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = '#1e2330')}
          >
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              background: color + '15', border: `1px solid ${color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px', flexShrink: 0,
            }}>
              {tmpl.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>{tmpl.name}</div>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
                  {tmpl.isPublic === false && (
                    <span style={{ fontSize: '10px', color: '#10b981', background: '#10b98115', padding: '2px 7px', borderRadius: '8px', fontWeight: '600' }}>
                      🔒 Mine
                    </span>
                  )}
                  <span style={{ fontSize: '11px', color: '#475569', background: '#1e2330', padding: '2px 8px', borderRadius: '10px' }}>
                    {tmpl.triggers?.length || 0} triggers
                  </span>
                </div>
              </div>
              <div style={{ color: '#475569', fontSize: '12px', marginTop: '3px', lineHeight: '1.5' }}>
                {tmpl.description}
              </div>
              {tmpl.industry && (
                <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '10px', color: color, background: color + '15', padding: '2px 7px', borderRadius: '8px' }}>
                  {tmpl.industry}
                </span>
              )}
            </div>
            <div style={{ flexShrink: 0, color: color, fontSize: '16px', marginTop: '8px' }}>→</div>
          </div>
        ))}
      </div>

      {!inline && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid #1e2330', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => setIsOpen(false)} style={{ padding: '8px 18px', background: '#1e2330', color: '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            Start from scratch
          </button>
        </div>
      )}
    </div>
  );

  if (!inline) {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '7px 14px', background: color + '15', color,
            border: `1px solid ${color}40`, borderRadius: '8px',
            cursor: 'pointer', fontSize: '12px', fontWeight: '600',
          }}
        >
          📋 Load template
        </button>
        {isOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999 }} onClick={() => setIsOpen(false)} />
            {panel}
          </>
        )}
      </>
    );
  }

  return panel;
}
